import { useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate, Link } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import PartnerLogin from "@/pages/partner/PartnerLogin";
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import PartnerSubmit from "@/pages/partner/PartnerSubmit";
import PartnerReferrals from "@/pages/partner/PartnerReferrals";
import { Toaster } from "@/components/ui/sonner";
import { LayoutDashboard, UserPlus, ClipboardList, LogOut, Loader2, Building2, Menu, X } from "lucide-react";

const NAV = [
  { to: "/partner", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/partner/submit", label: "Submit referral", icon: UserPlus },
  { to: "/partner/referrals", label: "Referrals", icon: ClipboardList },
];

const PawMark = () => (
  <span className="grid place-items-center w-9 h-9 rounded-[11px] bg-paw-primary shadow-[0_6px_14px_-6px_rgba(15,98,214,0.7)]">
    <svg viewBox="0 0 24 24" fill="#fff" className="w-5 h-5" aria-hidden="true">
      <circle cx="6" cy="10" r="2.1" /><circle cx="10.5" cy="6.5" r="2.1" /><circle cx="15.5" cy="6.5" r="2.1" /><circle cx="19" cy="10.5" r="1.9" />
      <path d="M12.5 12c-2.4 0-4.4 1.7-5 3.8-.5 1.7.9 3.2 2.7 3.2.9 0 1.6-.4 2.3-.4s1.4.4 2.3.4c1.8 0 3.2-1.5 2.7-3.2-.6-2.1-2.6-3.8-5-3.8z" />
    </svg>
  </span>
);

function PartnerShell() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (loading) return <div className="min-h-screen bg-paw-cream flex items-center justify-center"><Loader2 className="w-8 h-8 text-paw-primary/40 animate-spin" /></div>;
  if (!user || user === false || !["insurance_partner", "admin"].includes(user.role)) {
    return <PartnerLogin onLogin={() => navigate("/partner", { replace: true })} />;
  }

  const handleLogout = async () => { await logout(); navigate("/partner", { replace: true }); };

  return (
    <div className="min-h-screen bg-paw-cream text-paw-text flex">
      {/* Mobile topbar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-white border-b border-paw-border flex items-center justify-between px-4">
        <button onClick={() => setOpen(true)} className="p-2 -ml-2 rounded-lg hover:bg-black/5"><Menu className="w-5 h-5" /></button>
        <div className="flex items-center gap-2"><PawMark /><span className="text-xs text-paw-text-muted">Partner</span></div>
        <div className="w-9" />
      </header>

      {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-paw-border flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="px-6 py-6 border-b border-paw-border flex items-center justify-between">
          <Link to="/partner" className="flex items-center gap-2.5">
            <PawMark />
            <div>
              <span className="font-extrabold text-[18px] tracking-tight"><span className="text-paw-navy">Paw</span><span className="text-paw-primary">Linx</span></span>
              <p className="text-[11px] text-paw-text-muted mt-0.5 flex items-center gap-1"><Building2 className="w-3 h-3 text-paw-primary" /> Partner Portal</p>
            </div>
          </Link>
          <button onClick={() => setOpen(false)} className="lg:hidden p-1 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-paw-blue-50 text-paw-primary" : "text-paw-text-secondary hover:text-paw-navy hover:bg-paw-cream"}`}>
              <Icon className="w-4 h-4" strokeWidth={1.8} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-paw-border">
          <div className="px-3 py-2">
            <p className="text-xs text-paw-text-muted">Signed in as</p>
            <p className="text-sm text-paw-navy font-medium truncate">{user.first_name} {user.last_name}</p>
            <p className="text-xs text-paw-text-muted mt-0.5 truncate">{user.email}</p>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-paw-text-secondary hover:text-paw-navy hover:bg-paw-cream transition-colors">
            <LogOut className="w-4 h-4" strokeWidth={1.8} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden pt-14 lg:pt-0">
        <Routes>
          <Route path="/" element={<PartnerDashboard />} />
          <Route path="/partner" element={<PartnerDashboard />} />
          <Route path="/partner/submit" element={<PartnerSubmit />} />
          <Route path="/partner/referrals" element={<PartnerReferrals />} />
          <Route path="/partner/*" element={<Navigate to="/partner" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function PartnerLayout() {
  return (
    <AuthProvider>
      <PartnerShell />
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
